/**
 * PR1b Phase 2a — heal-wiring glue for the WHOLE-DOCUMENT import path + the
 * warn-only export gate.
 *
 * The pure CAD-core functions live in `@dxf-ai-editor/drawing-model`
 * (`healDrawingReferences` / `auditDocumentForExport`, PR1b Phase 1). This
 * module is the THIN apps/web adapter that:
 *   1. runs heal on a freshly-imported `DrawingDocument` BEFORE it is populated
 *      into the compact cache / metadata index and committed to the store
 *      (so the FIRST history baseline already reflects healed refs — undo can
 *      never step back to a dangling-ref state; plan §12 / Codex#2 P2-5);
 *   2. formats the bilingual (RU+EN, via the project `t()` mechanism) transcript
 *      summaries for both the import heal and the warn-only export audit.
 *
 * Heal is INITIAL-STATE NORMALISATION, not a Command-Engine mutation — it is
 * applied to the document as it becomes the import baseline, carries no undo
 * entry, and is never routed through `commitMutation`.
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md §12.
 */

import {
  healDrawingReferences,
  auditDocumentForExport,
  ReferenceErrorCode,
  type DrawingDocument,
  type DrawingEntity,
  type ReferenceAuditReport,
  type ReferenceFix,
} from "@dxf-ai-editor/drawing-model";

import { t } from "../i18n";
import {
  peekDocumentMemoryHolder,
  peekStreamedSpaceIds,
  snapshotSpaceEntities,
} from "./mutationStore";

/** Result of `healImportedDocument`: the healed doc + a ready-to-show summary. */
export interface ImportHealResult {
  /** Healed document — use THIS for populate + the store baseline. */
  document: DrawingDocument;
  /** Applied substitutions (empty for a clean doc — no spurious rewrites). */
  fixes: ReferenceFix[];
  /** Post-heal audit — `ok: false` iff unhealable refs (missing blocks) remain. */
  report: ReferenceAuditReport;
  /**
   * Bilingual transcript summary, or `null` when there is nothing to report
   * (clean doc: no fixes AND no residual errors). When non-null the caller
   * pushes it through `pushAuditEvent` with the supplied `tone`.
   */
  summary: string | null;
  /** "info" when every dangling ref was healed; "warning" if some remain. */
  tone: "info" | "warning";
}

/**
 * Run `healDrawingReferences` on a whole-document import and build the
 * transcript summary. PURE — never mutates the input (heal clones internally)
 * and never throws.
 */
export function healImportedDocument(
  document: DrawingDocument,
): ImportHealResult {
  const { document: healed, fixes, report } = healDrawingReferences(document);
  const unhealedCount = report.errors.length;
  const summary = buildHealSummary(fixes, unhealedCount);
  return {
    document: healed,
    fixes,
    report,
    summary,
    tone: unhealedCount > 0 ? "warning" : "info",
  };
}

/** Streaming heal result: a ready-to-show transcript summary + tone. */
export interface StreamingHealResult {
  /** Bilingual transcript summary, or `null` for a clean stream (no fixes). */
  summary: string | null;
  /** "info" — streaming heal applies only reserved-default substitutions. */
  tone: "info" | "warning";
}

/**
 * PR1b Phase 2b — build the transcript summary for a STREAMING import from the
 * per-chunk heal fixes the worker applied. Unlike `healImportedDocument`, the
 * streaming path has no post-heal whole-document report in hand (entities were
 * streamed + discarded), so unresolved-ref counting is left to the warn-only
 * EXPORT audit gate (which still runs over the final document). We therefore
 * report only the APPLIED fixes here — `unhealedCount: 0`. A clean stream
 * (no fixes) yields `summary: null` so the transcript stays silent.
 *
 * PURE — never mutates, never throws.
 */
export function buildStreamingHealResult(
  fixes: ReferenceFix[],
): StreamingHealResult {
  const summary = buildHealSummary(fixes, 0);
  // Streaming heal only ever substitutes reserved defaults (layer→0, …); it
  // never leaves a worse state, so the tone is informational. Residual
  // unfixable refs (missing blocks) surface via the export-gate warning.
  return { summary, tone: "info" };
}

/**
 * Build the bilingual heal summary string from the applied fixes + the count of
 * refs that could NOT be healed. Returns `null` when there is nothing to report
 * (no fixes and no residual errors) so a clean import stays silent.
 *
 * Shape (RU): "Восстановлено ссылок: 3 (слой→0: 2, тип линий→Continuous: 1)"
 *             with an optional "; не восстановлено: N" tail.
 */
export function buildHealSummary(
  fixes: ReferenceFix[],
  unhealedCount: number,
): string | null {
  if (fixes.length === 0 && unhealedCount === 0) return null;

  // When nothing was healed but refs remain unresolved (e.g. only a missing
  // block), surface a bare "unresolved: N" note rather than a "0 repairs" line.
  if (fixes.length === 0) {
    // `unhealed` template begins with "; " — trim the leading separator.
    return t("reference.heal.unhealed", { count: unhealedCount })
      .replace(/^;\s*/, "")
      .replace(/^./, (c) => c.toUpperCase());
  }

  const counts = countFixesByKind(fixes);
  const breakdown = KIND_ORDER.filter((kind) => counts[kind] > 0)
    .map((kind) =>
      t("reference.heal.breakdown_item", {
        label: t(KIND_LABEL_KEY[kind]),
        n: counts[kind],
      }),
    )
    .join(", ");

  let summary = t("reference.heal.summary", {
    count: fixes.length,
    breakdown,
  });
  if (unhealedCount > 0) {
    summary += t("reference.heal.unhealed", { count: unhealedCount });
  }
  return summary;
}

/**
 * PR1b Codex P2 (iter-3 + FIX-3/FIX-4) — SHARED per-space export snapshot
 * builder used by BOTH export paths (`editorStore.exportDxfText` store action
 * AND App.tsx `buildExportedDxfText` download/copy/preview). Single-source so
 * the warn-only audit gate and the serialiser ALWAYS see the SAME effective
 * entity source.
 *
 * ── The per-space source rule (FIX-3 + FIX-4) ──
 * Decide PER SPACE by STREAMING-PROVENANCE — NOT by a global
 * `cachePopulatedOnce` flag and NOT by inline-emptiness:
 *
 *   • A space whose inline entities were DISCARDED by the streaming importer
 *     (`discardEntitiesAfterStreaming: true`, recorded per-chunk via
 *     `markSpaceStreamed` → `holder.streamedSpaceIds`) → the CACHE is its
 *     authoritative/complete source. Emit the cache snapshot for it EVEN WHEN
 *     EMPTY `[]` (FIX-3): a streamed space drained to empty must surface `[]`,
 *     not the stale retained inline shell (e.g. a paper space that retains only
 *     an inline Viewport).
 *
 *   • A space ABSENT from `streamedSpaceIds` was imported INLINE (legacy /
 *     bulk-populate path) → its `space.entities[]` is the FULL-FIDELITY export
 *     source (FIX-4). The compact base store does NOT preserve per-entity
 *     `linetypeScale` (DXF group 48), so feeding a compact cache snapshot here
 *     would silently DROP linetypeScale even though the inline entity still
 *     carries it. Such a space is OMITTED from the map → the exporter's
 *     `snapshot ?? space.entities` contract falls back to the inline array.
 *
 * Why `cachePopulatedOnce` alone is wrong (FIX-4): an inline bulk import ALSO
 * flips `cachePopulatedOnce` (via `populateCompactCacheFromDocument`), so it
 * cannot tell an inline-imported space from a streaming-discarded one.
 *
 * Why inline-emptiness alone is wrong (FIX-3): a partially-retained paper space
 * has a NON-EMPTY inline Viewport yet is cache-authoritative, while a clean
 * empty model space is inline-authoritative. Only the EXPLICIT per-space
 * `streamedSpaceIds` marker separates the two.
 *
 * Returns `undefined` when NO space was streaming-discarded (legacy inline /
 * small-drawing path) — callers then export the inline arrays directly.
 */
export function buildExportEntitiesSnapshot(
  document: DrawingDocument,
): Map<string, DrawingEntity[]> | undefined {
  const holder = peekDocumentMemoryHolder(document.id);
  if (holder === undefined) {
    // No holder — inline arrays are authoritative (legacy path).
    return undefined;
  }
  const streamedSpaceIds = peekStreamedSpaceIds(document.id);
  if (streamedSpaceIds.size === 0) {
    // No space was streaming-discarded — every space's inline `space.entities`
    // is full-fidelity (preserves linetypeScale / group 48). FIX-4: do NOT
    // prefer the lossy compact cache for an inline import.
    return undefined;
  }
  const snapshot = new Map<string, DrawingEntity[]>();
  for (const space of document.spaces) {
    // Skip block-test / block-edit transient spaces — the exporter skips them
    // (DYN-1.9), so populating the map for them is wasted work.
    if (space.kind === "block-test" || space.kind === "block-edit") {
      continue;
    }
    // Only override the inline array for spaces whose inline entities were
    // streaming-discarded. The cache is authoritative for these — emit its
    // snapshot EVEN WHEN EMPTY (FIX-3: a drained streamed space surfaces `[]`,
    // never the stale retained inline shell). Non-streamed spaces are omitted
    // → the exporter falls back to full-fidelity `space.entities` (FIX-4).
    if (!streamedSpaceIds.has(space.id)) {
      continue;
    }
    snapshot.set(space.id, snapshotSpaceEntities(document.id, space.id));
  }
  return snapshot;
}

/**
 * Warn-only export gate (plan §9 OQ2): audit the document BEFORE export and
 * return a warning summary if unresolved references remain — `null` otherwise.
 * NEVER blocks the save; the caller surfaces the string (if any) via the
 * transcript and proceeds with the export regardless.
 *
 * PR1b Codex P2 — for a STREAMED file the shell `document.spaces[].entities`
 * are intentionally EMPTY (entities live in the compact cache); the exporter
 * reads them from a per-space `entitiesSnapshot` map instead (Phase 4 Wave 1
 * Task C). Auditing only the empty shell would MISS a streamed entity with an
 * unfixable ref (e.g. INSERT → missing block), so the export could silently
 * write a dangling ref the warning never reported. When `entitiesSnapshot` is
 * supplied, we audit the SAME effective source the exporter writes — the cache
 * snapshot materialised into the document — so the warning reflects what
 * actually gets serialised. Mirrors the exporter's `snapshot ?? space.entities`
 * fallback exactly: spaces present in the map use the snapshot; absent spaces
 * keep their inline array.
 */
export function buildExportAuditWarning(
  document: DrawingDocument,
  entitiesSnapshot?: Map<string, DrawingEntity[]>,
): string | null {
  const effective =
    entitiesSnapshot !== undefined
      ? materializeDocumentForAudit(document, entitiesSnapshot)
      : document;
  const report = auditDocumentForExport(effective);
  if (report.ok) return null;
  return t("reference.export.audit_warning", { count: report.errors.length });
}

/**
 * Build an audit-only document whose per-space `entities` mirror the exporter's
 * effective source: `entitiesSnapshot.get(space.id) ?? space.entities` (the
 * EXACT fallback `exportDrawingDocumentToDxfString` uses). Shallow per-space
 * clone — the input document and its inline arrays are never mutated. The
 * returned doc is for the read-only audit pass only.
 */
function materializeDocumentForAudit(
  document: DrawingDocument,
  entitiesSnapshot: Map<string, DrawingEntity[]>,
): DrawingDocument {
  return {
    ...document,
    spaces: document.spaces.map((space) => {
      const snapshot = entitiesSnapshot.get(space.id);
      if (snapshot === undefined) return space;
      return { ...space, entities: snapshot };
    }),
  };
}

/* ────────────────────────────────────────────────────────────────────────
 * Internals — fix-kind bucketing.
 * ──────────────────────────────────────────────────────────────────────── */

/** The heal-target kinds, in a stable display order. */
type HealKind =
  | "layer"
  | "textstyle"
  | "dimstyle"
  | "mleaderstyle"
  | "linetype"
  | "annoscale";

const KIND_ORDER: HealKind[] = [
  "layer",
  "textstyle",
  "dimstyle",
  "mleaderstyle",
  "linetype",
  "annoscale",
];

const KIND_LABEL_KEY = {
  layer: "reference.heal.kind.layer",
  textstyle: "reference.heal.kind.textstyle",
  dimstyle: "reference.heal.kind.dimstyle",
  mleaderstyle: "reference.heal.kind.mleaderstyle",
  linetype: "reference.heal.kind.linetype",
  annoscale: "reference.heal.kind.annoscale",
} as const satisfies Record<HealKind, string>;

/** Map a healed error code onto its display kind. */
function kindForCode(code: ReferenceErrorCode): HealKind | undefined {
  switch (code) {
    case ReferenceErrorCode.UNDEFINED_LAYER:
      return "layer";
    case ReferenceErrorCode.UNDEFINED_TEXT_STYLE:
      return "textstyle";
    case ReferenceErrorCode.UNDEFINED_DIMENSION_STYLE:
      return "dimstyle";
    case ReferenceErrorCode.UNDEFINED_MULTILEADER_STYLE:
      return "mleaderstyle";
    case ReferenceErrorCode.UNDEFINED_LINETYPE:
      return "linetype";
    case ReferenceErrorCode.UNDEFINED_ANNOTATION_SCALE:
      return "annoscale";
    default:
      return undefined;
  }
}

function countFixesByKind(fixes: ReferenceFix[]): Record<HealKind, number> {
  const counts: Record<HealKind, number> = {
    layer: 0,
    textstyle: 0,
    dimstyle: 0,
    mleaderstyle: 0,
    linetype: 0,
    annoscale: 0,
  };
  for (const fix of fixes) {
    const kind = kindForCode(fix.code);
    if (kind) counts[kind] += 1;
  }
  return counts;
}
